import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogTitle } from '../../../../components/ui/Dialog';
import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { useScheduledShifts } from '../../../../hooks/useScheduling';
import { Skeleton } from '../../../../components/ui/Skeleton';

// Week / month grid placeholder shown while shift data is loading. Mirrors
// the actual grid (7-col header + 1 row for week, 5 rows for month) so the
// surrounding card height stays stable.
const ScheduleSkeleton = ({ rows }) => (
  <div className="grid grid-cols-7 gap-2" aria-busy="true" aria-live="polite">
    {Array.from({ length: 7 }).map((_, i) => (
      <Skeleton key={`sh-${i}`} className="h-4 w-12 mx-auto rounded" />
    ))}
    {Array.from({ length: rows * 7 }).map((_, i) => (
      <Skeleton key={`sc-${i}`} className="h-20 w-full rounded-lg" />
    ))}
  </div>
);

export const WorkScheduleCalendar = ({ userId: userIdProp, rtl: rtlProp } = {}) => {
  const { id } = useParams();
  const { t } = useTranslation();

  const viewerUserId = useSelector((state) => state.user.userId);
  const rtlFromStore = useSelector((state) => state.user.rtl);

  const [viewMode, setViewMode] = useState('week');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [detailsShift, setDetailsShift] = useState(null);

  const effectiveUserId = userIdProp || id || viewerUserId;
  const rtl = rtlProp ?? rtlFromStore;

  const dateRange = useMemo(() => {
    if (viewMode === 'week') {
      const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    }
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);
    return eachDayOfInterval({ start, end });
  }, [selectedDate, viewMode]);

  const { shifts: scheduledShifts, loading: shiftsLoading } = useScheduledShifts(dateRange, {
    userId: effectiveUserId,
  });

  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return null;
    const parts = timeStr.split(':');
    if (parts.length < 2) return null;
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
    return hours * 60 + minutes;
  };

  const formatMinutesToTime = (minsTotal) => {
    let m = Number(minsTotal);
    if (!Number.isFinite(m)) return '';
    m = ((m % 1440) + 1440) % 1440;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
  };

  const getBreakRulesText = (shift) => {
    const base = shift?.__sourceShift || shift;
    const startMin = parseTimeToMinutes(base?.startTime);
    const endMin = parseTimeToMinutes(base?.endTime);
    if (startMin === null || endMin === null) return null;

    const breakStartMinRaw = parseTimeToMinutes(base?.breakStartTime);
    const breakEndMinRaw = parseTimeToMinutes(base?.breakEndTime);
    const durationRaw = (base?.breakDurationMinutes != null && Number(base.breakDurationMinutes) > 0)
      ? Number(base.breakDurationMinutes)
      : null;

    const hasAnyBreakConfig = breakStartMinRaw !== null || breakEndMinRaw !== null || durationRaw !== null;
    if (!hasAnyBreakConfig) return { summary: t('BreakNone', { defaultValue: 'Break: none' }) };

    const overnight = endMin <= startMin;
    const shiftEndCalc = overnight ? (endMin + 1440) : endMin;

    const adjust = (m) => {
      if (m === null) return null;
      let v = m;
      if (overnight && v < startMin) v += 1440;
      return v;
    };

    const breakStartCalc = adjust(breakStartMinRaw);
    const breakEndCalc = adjust(breakEndMinRaw);

    const windowStartCalc = breakStartCalc ?? startMin;
    const windowEndCalc = breakEndCalc ?? shiftEndCalc;

    let durationEffective = durationRaw;
    let inferred = false;
    if ((durationEffective == null || durationEffective <= 0) && breakStartCalc != null && breakEndCalc != null) {
      const inferredDur = Math.floor(windowEndCalc - windowStartCalc);
      if (inferredDur > 0) {
        durationEffective = inferredDur;
        inferred = true;
      }
    }

    if (durationEffective == null || durationEffective <= 0) {
      return {
        summary: t('BreakNotConfiguredDurationMissing', { defaultValue: 'Break: not configured (duration missing)' }),
      };
    }

    const windowLabel = (breakStartMinRaw !== null || breakEndMinRaw !== null)
      ? `${formatMinutesToTime(windowStartCalc)} – ${formatMinutesToTime(windowEndCalc)}`
      : t('during shift', { defaultValue: 'during shift' });

    const durationLabel = inferred
      ? `${durationEffective} min (${t('inferred', { defaultValue: 'inferred' })})`
      : `${durationEffective} min`;

    return {
      summary: windowLabel,
      details: `${t('Duration', { defaultValue: 'Duration' })}: ${durationLabel}`,
    };
  };

  const isOvernightShift = (shift) => {
    const start = parseTimeToMinutes(shift?.startTime);
    const end = parseTimeToMinutes(shift?.endTime);
    if (start === null || end === null) return false;
    return end <= start;
  };

  const expandedShifts = useMemo(() => {
    const result = [];
    (scheduledShifts || []).forEach((shift) => {
      if (!shift?.date || !shift?.startTime || !shift?.endTime || !isOvernightShift(shift)) {
        result.push(shift);
        return;
      }

      const nextDayKey = format(addDays(new Date(`${shift.date}T00:00:00`), 1), 'yyyy-MM-dd');
      const sourceKey = shift?.id ?? `${shift?.date}-${shift?.startTime}-${shift?.endTime}-${shift?.origin || ''}`;

      result.push({
        ...shift,
        __sourceShift: shift,
        __overnightSegment: 'start',
        __overnightStartTime: shift.startTime,
        __overnightEndTime: shift.endTime,
        __segmentKey: `${sourceKey}-overnight-start`,
      });

      result.push({
        ...shift,
        date: nextDayKey,
        __sourceShift: shift,
        __overnightSegment: 'end',
        __overnightStartTime: shift.startTime,
        __overnightEndTime: shift.endTime,
        __segmentKey: `${sourceKey}-overnight-end`,
      });
    });
    return result;
  }, [scheduledShifts]);

  const shiftsByDate = useMemo(() => {
    const map = new Map();
    expandedShifts.forEach((shift) => {
      const key = shift?.date;
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(shift);
    });

    map.forEach((arr) => {
      arr.sort((a, b) => {
        const aStart = a?.__overnightSegment === 'end' ? 0 : (parseTimeToMinutes(a?.startTime) ?? 0);
        const bStart = b?.__overnightSegment === 'end' ? 0 : (parseTimeToMinutes(b?.startTime) ?? 0);
        return aStart - bStart;
      });
    });

    return map;
  }, [expandedShifts]);

  const getShiftsForDate = (date) => {
    const key = format(date, 'yyyy-MM-dd');
    return shiftsByDate.get(key) || [];
  };

  const getTimeRangeLabel = (shift) => {
    if (shift?.__overnightSegment === 'start') {
      return `${shift?.__overnightStartTime} → ${shift?.__overnightEndTime}`;
    }
    if (shift?.__overnightSegment === 'end') {
      return `→ ${shift?.__overnightEndTime}`;
    }
    return `${shift?.startTime} - ${shift?.endTime}`;
  };

  const getOriginBadge = (shift) => {
    if (shift?.origin === 'Rule') {
      return (
        <div className="flex items-center gap-1 text-xs text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
          <span>🔄</span>
          <span>{t('Recurring')}</span>
        </div>
      );
    }
    if (shift?.origin === 'ShiftSetEmbedded') {
      return (
        <div className="flex items-center gap-1 text-xs text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
          <span>📋</span>
          <span>{shift?.shiftSetName || t('Set')}</span>
        </div>
      );
    }
    if (shift?.origin === 'Manual' || shift?.origin === 'ManualOverride') {
      return (
        <div className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded">
          <span>✏️</span>
          <span>{t('One-Time')}</span>
        </div>
      );
    }
    return null;
  };

  const handleNavigateDate = (direction) => {
    if (viewMode === 'week') {
      setSelectedDate(direction === 'next' ? addDays(selectedDate, 7) : addDays(selectedDate, -7));
    } else {
      const newDate = new Date(selectedDate);
      newDate.setMonth(selectedDate.getMonth() + (direction === 'next' ? 1 : -1));
      setSelectedDate(newDate);
    }
  };

  const renderWeekView = () => {
    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return (
      <div className="p-6 overflow-x-auto">
        <div className="grid grid-cols-7 gap-4 min-w-[700px]">
          {weekDays.map((day, index) => (
            <div key={day} className="text-center">
              <div className="text-sm font-medium text-foreground mb-2">{day}</div>
              <div className="text-xs text-muted-foreground">
                {format(dateRange[index], 'd MMM')}
              </div>
            </div>
          ))}

          {dateRange.map((date, index) => {
            const dayShifts = getShiftsForDate(date);
            const isToday = isSameDay(date, new Date());

            return (
              <div
                key={index}
                className={`min-h-[200px] border rounded-lg p-3 ${
                  isToday ? 'border-primary border-2 bg-primary/5' : 'border-border bg-card'
                }`}
              >
                <div className="space-y-2">
                  {dayShifts.length === 0 ? (
                    <div className="w-full py-2 text-center text-muted-foreground text-sm">
                      {t('No shifts')}
                    </div>
                  ) : (
                    dayShifts.map((shift, shiftIndex) => (
                      <div
                        key={shift?.__segmentKey || `${shift?.id || shiftIndex}`}
                        className={`p-2 rounded ${
                          shift?.isVirtual
                            ? 'border-2 border-dashed border-border bg-muted'
                            : 'border border-border bg-card'
                        }`}
                        role="button"
                        tabIndex={0}
                        onClick={() => setDetailsShift(shift?.__sourceShift || shift)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setDetailsShift(shift?.__sourceShift || shift);
                          }
                        }}
                        style={{
                          borderInlineStartWidth: '4px',
                          borderInlineStartColor: shift?.color || '#3b82f6',
                        }}
                        title={shift?.__overnightSegment ? `${t('Overnight shift')} (${shift?.__overnightStartTime} → ${shift?.__overnightEndTime})` : undefined}
                      >
                        <div className="text-xs font-medium text-foreground mb-1">
                          {getTimeRangeLabel(shift)}
                        </div>
                        {shift?.__overnightSegment && (
                          <div className="text-[10px] text-muted-foreground mb-1">
                            {shift?.__overnightSegment === 'end' ? t('Overnight (cont.)') : t('Overnight')}
                          </div>
                        )}
                        {(() => {
                          const br = getBreakRulesText(shift);
                          if (!br) return null;
                          return (
                            <div className="mb-1 border border-border rounded bg-muted px-2 py-1">
                              <div className="text-[11px] font-semibold text-foreground">{t('Break', { defaultValue: 'Break' })}</div>
                              <div className="text-xs text-muted-foreground">{br.summary}</div>
                              {br.details ? (
                                <div className="text-[11px] text-muted-foreground">{br.details}</div>
                              ) : null}
                            </div>
                          );
                        })()}
                        {getOriginBadge(shift)}
                        {shift?.isVirtual && (
                          <div className="mt-1 text-xs text-muted-foreground italic">{t('Virtual')}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const weeks = [];

    for (let i = 0; i < dateRange.length; i += 7) {
      weeks.push(dateRange.slice(i, i + 7));
    }

    return (
      <div className="p-6 overflow-x-auto">
        <div className="grid grid-cols-7 gap-2 mb-2 min-w-[700px]">
          {weekDays.map((day) => (
            <div key={day} className="text-center text-sm font-medium text-foreground py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="space-y-2 min-w-[700px]">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 gap-2">
              {week.map((date, dayIndex) => {
                const dayShifts = getShiftsForDate(date);
                const isToday = isSameDay(date, new Date());

                return (
                  <div
                    key={dayIndex}
                    className={`min-h-[120px] border rounded p-2 ${
                      isToday ? 'border-primary border-2 bg-primary/5' : 'border-border bg-card'
                    }`}
                  >
                    <div className="text-sm font-medium text-foreground mb-1">
                      {format(date, 'd')}
                    </div>
                    <div className="space-y-1">
                      {dayShifts.slice(0, 2).map((shift, shiftIndex) => (
                        <div
                          key={shift?.__segmentKey || `${shift?.id || shiftIndex}`}
                          className={`text-xs p-1 rounded ${
                            shift?.isVirtual
                              ? 'border border-dashed border-border bg-muted'
                              : 'bg-primary/10 text-primary'
                          }`}
                          role="button"
                          tabIndex={0}
                          onClick={() => setDetailsShift(shift?.__sourceShift || shift)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setDetailsShift(shift?.__sourceShift || shift);
                            }
                          }}
                          title={shift?.__overnightSegment ? `${t('Overnight shift')} (${shift?.__overnightStartTime} → ${shift?.__overnightEndTime})` : undefined}
                        >
                          <div className="font-medium truncate">
                            {shift?.__overnightSegment === 'start'
                              ? `${shift?.__overnightStartTime}→`
                              : shift?.__overnightSegment === 'end'
                                ? `→${shift?.__overnightEndTime}`
                                : shift?.startTime}
                          </div>
                        </div>
                      ))}
                      {dayShifts.length > 2 && (
                        <div className="text-xs text-muted-foreground text-center">
                          +{dayShifts.length - 2} {t('more')}
                        </div>
                      )}
                      {dayShifts.length === 0 && (
                        <div className="text-xs text-muted-foreground text-center py-1">
                          {t('No shifts')}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border">
      <div className="px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => handleNavigateDate('prev')}
              className="p-2 hover:bg-muted rounded-md transition-colors"
              aria-label={t('Previous')}
            >
              {rtl ? '→' : '←'}
            </button>
            <h3 className="text-lg font-semibold text-foreground">
              {viewMode === 'week'
                ? t('WeekOf', { date: format(startOfWeek(selectedDate, { weekStartsOn: 1 }), 'MMM d, yyyy') })
                : format(selectedDate, 'MMMM yyyy')}
            </h3>
            <button
              onClick={() => handleNavigateDate('next')}
              className="p-2 hover:bg-muted rounded-md transition-colors"
              aria-label={t('Next')}
            >
              {rtl ? '←' : '→'}
            </button>
            <button
              onClick={() => setSelectedDate(new Date())}
              className="px-3 py-1 text-sm border border-border rounded-md hover:bg-muted transition-colors"
            >
              {t('Today')}
            </button>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center bg-muted rounded-md p-1">
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                  viewMode === 'week' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('Week')}
              </button>
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                  viewMode === 'month' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('Month')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {shiftsLoading ? (
        <ScheduleSkeleton rows={viewMode === 'week' ? 1 : 5} />
      ) : (
        viewMode === 'week' ? renderWeekView() : renderMonthView()
      )}

      <Dialog open={!!detailsShift} onOpenChange={() => setDetailsShift(null)} size="sm">
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Shift details', { defaultValue: 'Shift details' })}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-2">
            <div className="text-sm text-foreground">
              <span className="font-medium">{t('Date', { defaultValue: 'Date' })}:</span>{' '}
              {detailsShift?.date || ''}
            </div>
            <div className="text-sm text-foreground">
              <span className="font-medium">{t('Time', { defaultValue: 'Time' })}:</span>{' '}
              {detailsShift?.startTime} – {detailsShift?.endTime}
            </div>
            {(() => {
              const br = getBreakRulesText(detailsShift);
              if (!br) return null;
              return (
                <div className="border border-border rounded bg-muted px-3 py-2">
                  <div className="text-xs font-semibold text-foreground">{t('Break', { defaultValue: 'Break' })}</div>
                  <div className="text-sm text-foreground mt-1">{br.summary}</div>
                  {br.details ? (
                    <div className="text-xs text-muted-foreground mt-1">{br.details}</div>
                  ) : null}
                </div>
              );
            })()}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkScheduleCalendar;
